/**
 * Die Führungsanzeige: oben das nächste Manöver, unten Tempo, Tempolimit und
 * was noch bleibt. Sie legt sich über die Karte und schaltet die Seitenleiste
 * beiseite - beim Fahren zählt ein Blick, nicht eine Liste.
 */
import { createNavigation, strecke, dauer } from '../data/navigation.js';
import { routeVehicle, fetchSpeedLimits } from '../data/routing.js';
import { tankstellenEntlang, parkenBei } from '../data/unterwegs.js';

/** Valhalla-Manövertypen -> Pfeil. Nur die Richtung, nicht jede Feinheit. */
const PFEILE = {
  1: '↑', 2: '↑', 3: '↑', 4: '◆', 5: '◆', 6: '◆',
  9: '↗', 10: '→', 11: '↘', 12: '↱', 13: '↰',
  14: '↙', 15: '←', 16: '↖', 17: '↑', 18: '↑',
  19: '↗', 20: '↖', 21: '↗', 22: '↑', 23: '↗', 24: '↖',
  25: '↱', 26: '⟳', 27: '⤷', 37: '↑'
};

const SPRACHE = 'de-CH';

export function createNavOverlay({ map, onStart, onEnd }) {
  let nav = null;
  let root = null;
  let ziel = null;
  let modus = 'auto';
  let stumm = false;
  let letzteAnsage = '';
  let tankstellen = [];       // mit `entlang` versehen, nach Strecke sortiert
  let parkplaetze = null;     // null = noch nicht geholt
  let parkenGezeigt = false;
  const el = {};

  function sprich(text) {
    if (stumm || !text || text === letzteAnsage) return;
    letzteAnsage = text;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = SPRACHE;
      u.rate = 1.05;
      speechSynthesis.cancel();
      speechSynthesis.speak(u);
    } catch { /* ohne Stimme bleibt die Anzeige */ }
  }

  function bauen() {
    root = document.createElement('div');
    root.className = 'nav';

    const oben = document.createElement('div');
    oben.className = 'nav__banner';
    el.pfeil = document.createElement('div');
    el.pfeil.className = 'nav__arrow';
    const textbox = document.createElement('div');
    textbox.className = 'nav__texts';
    el.distanz = document.createElement('div');
    el.distanz.className = 'nav__dist';
    el.anweisung = document.createElement('div');
    el.anweisung.className = 'nav__instr';
    textbox.append(el.distanz, el.anweisung);
    oben.append(el.pfeil, textbox);

    const unten = document.createElement('div');
    unten.className = 'nav__bar';

    el.tempo = document.createElement('div');
    el.tempo.className = 'nav__speed';
    el.tempo.innerHTML = '<strong>–</strong><span>km/h</span>';

    el.limit = document.createElement('div');
    el.limit.className = 'nav__limit';
    el.limit.hidden = true;

    el.rest = document.createElement('div');
    el.rest.className = 'nav__rest';

    const knoepfe = document.createElement('div');
    knoepfe.className = 'nav__btns';
    el.ton = document.createElement('button');
    el.ton.type = 'button';
    el.ton.className = 'nav__btn';
    el.ton.textContent = 'Ton aus';
    el.ton.addEventListener('click', () => {
      stumm = !stumm;
      el.ton.textContent = stumm ? 'Ton an' : 'Ton aus';
      if (stumm) speechSynthesis.cancel();
    });
    const schluss = document.createElement('button');
    schluss.type = 'button';
    schluss.className = 'nav__btn nav__btn--end';
    schluss.textContent = 'Beenden';
    schluss.addEventListener('click', () => beenden());
    knoepfe.append(el.ton, schluss);

    unten.append(el.tempo, el.limit, el.rest, knoepfe);

    // Unterwegs-Kasten: klein und zu, bis man ihn braucht. Nur beim Auto -
    // zu Fuss und mit dem Velo sucht niemand eine Tankstelle.
    el.box = document.createElement('div');
    el.box.className = 'nav__box';
    el.box.hidden = true;
    el.boxListe = document.createElement('div');
    el.boxListe.className = 'nav__box-list';
    el.boxListe.hidden = true;

    el.knopfTanken = kastenKnopf('Tanken', 'tanken');
    el.knopfParken = kastenKnopf('Parken', 'parken');
    const reihe = document.createElement('div');
    reihe.className = 'nav__box-tabs';
    reihe.append(el.knopfTanken, el.knopfParken);
    el.box.append(reihe, el.boxListe);

    root.append(oben, el.box, unten);
    document.body.append(root);
  }

  let letztePosition = null;
  let offen = null;          // 'tanken' | 'parken' | null

  function kastenKnopf(text, welches) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'nav__box-tab';
    b.textContent = text;
    b.addEventListener('click', () => {
      offen = offen === welches ? null : welches;
      if (offen === 'parken' && parkplaetze === null) parkenHolen();
      zeichneKasten();
    });
    return b;
  }

  /** Inhalt des Kastens - je nachdem, was offen ist. */
  function zeichneKasten() {
    if (!el.box) return;
    el.box.hidden = modus !== 'auto';
    el.knopfTanken.classList.toggle('is-open', offen === 'tanken');
    el.knopfParken.classList.toggle('is-open', offen === 'parken');
    el.boxListe.hidden = !offen;
    if (!offen) return;

    el.boxListe.textContent = '';
    const eintraege = offen === 'tanken'
      ? tankstellen.filter((t) => t.entlang > letzterFortschritt + 150).slice(0, 6)
      : (parkplaetze || []).slice(0, 6);

    if (!eintraege.length) {
      const leer = document.createElement('div');
      leer.className = 'nav__box-empty';
      leer.textContent = offen === 'tanken'
        ? 'Keine Tankstelle mehr voraus.'
        : (parkplaetze === null ? 'Suche …' : 'Nichts gefunden.');
      el.boxListe.append(leer);
      return;
    }

    for (const e of eintraege) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'nav__box-item';
      const name = document.createElement('strong');
      name.textContent = e.name;
      const sub = document.createElement('small');
      sub.textContent = offen === 'tanken'
        ? `${strecke(e.entlang - letzterFortschritt)} voraus${e.zeiten === '24/7' ? ' · 24 h' : ''}`
        : (() => {
            const art = e.ueberdacht ? 'Parkhaus' : 'Parkplatz';
            // Trägt der Eintrag keinen Namen, steht die Art schon in der
            // Überschrift - dann nicht zweimal dasselbe.
            return [e.name === art ? null : art,
                    e.plaetze ? `${e.plaetze} Pl.` : null,
                    e.gebuehr === 'gratis' ? 'gratis' : null]
              .filter(Boolean).join(' · ') || 'ohne Angaben';
          })();
      b.append(name, sub);
      b.addEventListener('click', () => waehlen(e));
      el.boxListe.append(b);
    }
  }

  /**
   * Tankstelle = Zwischenhalt, die Fahrt geht danach weiter zum Ziel.
   * Parkhaus = neues Ziel, dort steigt man aus. Zwei verschiedene Absichten,
   * deshalb zwei verschiedene Wirkungen.
   */
  function waehlen(e) {
    offen = null;
    zeichneKasten();
    const punkt = { lat: e.lat, lng: e.lng };
    if (e.entlang !== undefined) {
      zwischenhalt = punkt;
      sprich(`Zwischenhalt ${e.name} eingefügt.`);
    } else {
      ziel = punkt;
      zwischenhalt = null;
      sprich(`Ziel geändert auf ${e.name}.`);
    }
    neuBerechnen(letztePosition || punkt);
  }

  let zwischenhalt = null;
  let letzterFortschritt = 0;

  function zeigen(d) {
    letztePosition = d.position;
    el.pfeil.textContent = PFEILE[d.manoever.typ] || '↑';
    el.distanz.textContent = strecke(d.bisManoever);
    el.anweisung.textContent = d.manoever.text;

    const kmh = d.tempoKmh;
    el.tempo.querySelector('strong').textContent =
      kmh === null ? '–' : String(Math.round(kmh));

    if (d.limit) {
      el.limit.hidden = false;
      el.limit.textContent = String(d.limit);
      // Nur einfärben, wenn die Messung auch taugt - unter 3 km/h ist sie Rauschen.
      const zuSchnell = kmh !== null && kmh > 3 && kmh > d.limit + 5;
      el.limit.classList.toggle('is-over', zuSchnell);
    } else {
      el.limit.hidden = true;
    }

    letzterFortschritt = d.entlang;
    if (offen === 'tanken') zeichneKasten();
    // Parkmöglichkeiten im Voraus holen, damit sie beim Antippen schon da sind.
    if (!parkenGezeigt && d.uebrig < 3000) { parkenGezeigt = true; parkenHolen(); }

    const ankunft = new Date(Date.now() + d.restSekunden * 1000);
    el.rest.textContent =
      `${strecke(d.uebrig)} · ${dauer(d.restSekunden)} · an ${String(ankunft.getHours()).padStart(2, '0')}:${String(ankunft.getMinutes()).padStart(2, '0')}`;

    // Karte mitführen: Blick in Fahrtrichtung, geneigt.
    map.easeTo({
      center: [d.position.lng, d.position.lat],
      bearing: d.kurs ?? map.getBearing(),
      pitch: 55,
      zoom: Math.max(16, map.getZoom()),
      duration: 900,
      essential: true
    });

    if (d.amZiel) {
      sprich('Sie haben Ihr Ziel erreicht.');
      setTimeout(beenden, 2500);
    }
  }

  /** Parkmöglichkeiten einmalig holen; der Kasten zeigt sie beim Öffnen. */
  async function parkenHolen() {
    if (parkplaetze !== null) return;
    parkplaetze = [];
    try {
      parkplaetze = await parkenBei(ziel);
    } catch { /* ohne Vorschläge bleibt die Führung */ }
    if (offen === 'parken') zeichneKasten();
  }

  async function neuBerechnen(von) {
    if (!ziel) return;
    sprich('Route wird neu berechnet.');
    try {
      const res = await routeVehicle({ from: von, to: ziel, via: zwischenhalt, costing: modus });
      const limits = await fetchSpeedLimits({ shape: res.shape, costing: modus }).catch(() => []);
      zeichneRoute(res.coordinates);
      nav.ersetzen({ ...res, limits });
    } catch {
      sprich('Neue Route nicht möglich.');
    }
  }

  function zeichneRoute(coordinates) {
    const data = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }]
    };
    if (map.getSource('route')) map.getSource('route').setData(data);
  }

  async function starten({ route, to, costing }) {
    ziel = to;
    modus = costing || 'auto';
    if (!route?.maneuvers?.length) return;

    if (!root) bauen();
    root.hidden = false;
    document.body.classList.add('is-navigating');
    onStart?.();

    // Tempolimits nachladen - die Fuehrung startet auch ohne sie.
    const limits = await fetchSpeedLimits({ shape: route.shape, costing: modus }).catch(() => []);

    nav = createNavigation({
      onUpdate: zeigen,
      onAnnounce: sprich,
      onOffRoute: neuBerechnen
    });
    nav.start({ ...route, limits });
    sprich(route.maneuvers[0]?.ansageJetzt || 'Navigation gestartet.');

    // Tankstellen einmalig holen und auf die Strecke projizieren. Luftlinie
    // hilft nicht - entscheidend ist, wann man an ihnen vorbeikommt.
    tankstellen = [];
    parkplaetze = null;
    parkenGezeigt = false;
    zwischenhalt = null;
    letzterFortschritt = 0;
    offen = null;
    zeichneKasten();
    try {
      const gefunden = await tankstellenEntlang(route.coordinates);
      tankstellen = gefunden
        .map((t) => {
          const p = nav.entlangWert(t);
          return p ? { ...t, entlang: p.entlang, abseits: p.abstand } : null;
        })
        .filter((t) => t && t.abseits < 400)
        .sort((a, b) => a.entlang - b.entlang);
      zeichneKasten();
    } catch { /* ohne Tankstellen führt die Navigation trotzdem */ }
  }

  function beenden() {
    nav?.stop();
    nav = null;
    speechSynthesis.cancel();
    if (root) root.hidden = true;
    document.body.classList.remove('is-navigating');
    map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
    onEnd?.();
  }

  return { starten, beenden, laeuft: () => Boolean(nav) };
}
